<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title>
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon> </v-btn
          >Add a Procedure
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="6">
              <v-text-field
                v-model="form.name"
                outlined
                label="Procedure Name *"
                prepend-inner-icon="mdi-clipboard-text-outline"
                :error-messages="errors.name"
                placeholder="e.g., Walking Exercise, Running Protocol"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.default_speed"
                outlined
                label="Default Speed (mph)"
                prepend-inner-icon="mdi-speedometer"
                type="number"
                step="0.1"
                min="0"
                max="25"
                :error-messages="errors.default_speed"
                placeholder="e.g., 3.5"
              ></v-text-field>
            </v-col>
            <v-col sm="12">
              <v-textarea
                v-model="form.description"
                outlined
                label="Description *"
                prepend-inner-icon="mdi-text"
                rows="3"
                :error-messages="errors.description"
                placeholder="Detailed description of the procedure, its purpose and expected outcomes..."
              ></v-textarea>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.max_speed"
                outlined
                label="Maximum Speed (mph)"
                prepend-inner-icon="mdi-speedometer-medium"
                type="number"
                step="0.1"
                min="0"
                max="25"
                :error-messages="errors.max_speed"
                placeholder="e.g., 8.0"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.default_unweighting"
                outlined
                label="Default Unweighting (%)"
                prepend-inner-icon="mdi-weight"
                type="number"
                step="0.1"
                min="0"
                max="100"
                :error-messages="errors.default_unweighting"
                placeholder="e.g., 50.0"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.max_heart_rate"
                outlined
                label="Maximum Heart Rate (bpm)"
                prepend-inner-icon="mdi-heart-pulse"
                type="number"
                min="60"
                max="220"
                :error-messages="errors.max_heart_rate"
                placeholder="e.g., 150"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-switch
                v-model="form.is_active"
                label="Active"
                color="primary"
                :false-value="false"
                :true-value="true"
              ></v-switch>
            </v-col>
          </v-row>
          
          <!-- Procedure Type Suggestions -->
          <v-row v-if="!form.name">
            <v-col sm="12">
              <v-card outlined class="pa-4 mt-2">
                <v-card-title class="text-h6">Quick Templates</v-card-title>
                <v-card-text>
                  <v-chip-group>
                    <v-chip 
                      v-for="template in procedureTemplates" 
                      :key="template.name"
                      @click="applyTemplate(template)"
                      color="primary"
                      outlined
                      small
                    >
                      {{ template.name }}
                    </v-chip>
                  </v-chip-group>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Preview Card -->
          <v-row v-if="form.name || form.description">
            <v-col sm="12">
              <v-card outlined class="pa-4 mt-4">
                <v-card-title class="text-h6">Preview</v-card-title>
                <v-card-text>
                  <p><strong>Name:</strong> {{ form.name || 'Not specified' }}</p>
                  <p><strong>Description:</strong> {{ form.description || 'Not specified' }}</p>
                  <p><strong>Speed Range:</strong> {{ formatSpeedRange() }}</p>
                  <p><strong>Max Heart Rate:</strong> {{ form.max_heart_rate ? form.max_heart_rate + ' bpm' : 'Not specified' }}</p>
                  <p><strong>Unweighting:</strong> {{ form.default_unweighting ? form.default_unweighting + '%' : 'Not specified' }}</p>
                  <p><strong>Intensity Level:</strong> {{ getIntensityLevel() }}</p>
                  <v-chip 
                    small 
                    :color="form.is_active ? 'green' : 'red'" 
                    text-color="white"
                  >
                    {{ form.is_active ? 'Active' : 'Inactive' }}
                  </v-chip>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row>
            <v-col sm="12" class="text-right">
              <v-btn
                :loading="isLoading"
                :disabled="isLoading"
                rounded
                color="primary"
                dark
                @click="save"
                class="mr-2"
              >
                Save Procedure
              </v-btn>
              <v-btn
                outlined
                color="secondary"
                rounded
                @click="resetForm"
              >
                Reset
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
export default {
  name: "ProcedureCreatePage",
  data() {
    return {
      form: {
        is_active: true,
        name: "",
        description: "",
        default_speed: "",
        max_speed: "",
        default_unweighting: "",
        max_heart_rate: "",
      },
      procedureTemplates: [
        {
          name: "Basic Walking",
          description: "Basic walking exercise for general fitness and mobility improvement",
          default_speed: "2.0",
          max_speed: "4.0",
          default_unweighting: "0",
          max_heart_rate: "130"
        },
        {
          name: "Rehabilitation Walk",
          description: "Gentle walking protocol for rehabilitation and recovery",
          default_speed: "1.5",
          max_speed: "3.0",
          default_unweighting: "50",
          max_heart_rate: "120"
        },
        {
          name: "Beginner Running",
          description: "Entry-level running protocol for new runners",
          default_speed: "4.0",
          max_speed: "6.0",
          default_unweighting: "20",
          max_heart_rate: "150"
        },
        {
          name: "Advanced Running",
          description: "High-intensity running protocol for athletes",
          default_speed: "8.0",
          max_speed: "12.0",
          default_unweighting: "5",
          max_heart_rate: "180"
        },
        {
          name: "Gait Training",
          description: "Specialized gait pattern correction and training",
          default_speed: "1.2",
          max_speed: "2.0",
          default_unweighting: "80",
          max_heart_rate: "115"
        },
        {
          name: "Endurance Training",
          description: "Extended duration exercise for cardiovascular endurance",
          default_speed: "5.0",
          max_speed: "7.5",
          default_unweighting: "15",
          max_heart_rate: "155"
        }
      ],
      isLoading: false,
    };
  },
  computed: {
    errors() {
      return this.$store.getters['errors/getErrors'];
    }
  },
  mounted() {
    // Any initialization if needed
  },
  methods: {
    async save() {
      try {
        this.isLoading = true;
        await axios.post("/procedures", this.form);
        this.isLoading = false;
        this.$router.push("/procedures");
      } catch (e) {
        console.error("Error saving procedure:", e);
        this.isLoading = false;
      }
    },
    applyTemplate(template) {
      this.form.name = template.name;
      this.form.description = template.description;
      this.form.default_speed = template.default_speed;
      this.form.max_speed = template.max_speed;
      this.form.default_unweighting = template.default_unweighting;
      this.form.max_heart_rate = template.max_heart_rate;
    },
    resetForm() {
      this.form = {
        is_active: true,
        name: "",
        description: "",
        default_speed: "",
        max_speed: "",
        default_unweighting: "",
        max_heart_rate: "",
      };
    },
    formatSpeedRange() {
      const defaultSpeed = this.form.default_speed;
      const maxSpeed = this.form.max_speed;
      
      if (!defaultSpeed && !maxSpeed) {
        return 'Not specified';
      } else if (defaultSpeed && !maxSpeed) {
        return defaultSpeed + ' mph (default)';
      } else if (!defaultSpeed && maxSpeed) {
        return 'Up to ' + maxSpeed + ' mph';
      } else {
        return defaultSpeed + ' - ' + maxSpeed + ' mph';
      }
    },
    getIntensityLevel() {
      const speed = parseFloat(this.form.default_speed) || 0;
      const heartRate = parseInt(this.form.max_heart_rate) || 0;

      if (speed <= 2.0 && heartRate <= 120) {
        return 'Low';
      } else if (speed <= 5.0 && heartRate <= 150) {
        return 'Moderate';
      } else if (speed <= 8.0 && heartRate <= 170) {
        return 'High';
      } else {
        return 'Very High';
      }
    }
  },
};
</script>